// MST
import { types, flow, cast, detach } from 'mobx-state-tree'

// Interfaces
import {
  ICertificationWebpageSupportDocumentApiDownloadDocumentResponse,
  ICertificationWebpageSupportDocumentApiListResponse,
  ICertificationWebpageSupportDocumentDownloadDocumentAttrs,
  ICertificationWebpageSupportDocumentGetListAttrs,
} from 'interfaces/webpage/certification/registration/precondition'
import { ISharedCommonLoadingAttrs } from 'interfaces/shared/shared-common.interface'

// Extensions
import { withHelper_commonLoadingModel, withHelper_commonMapLoading } from 'models/extensions/helper'
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_fileDownload } from 'models/extensions/with-helper'

// Lodash
import cloneDeep from 'lodash/cloneDeep'

// Constant
import { LOADING } from 'constant/shared.constant'

// Types
const { maybeNull, model, optional, array, number, string } = types

// Loading
const { BASE, DOWNLOAD } = LOADING

const documentSupportProps = {
  id: maybeNull(number),
  preconditionTypeName: maybeNull(string),
  userOriginalFileName: maybeNull(string),
  userFileBlobId: maybeNull(string),
  loading: withHelper_commonLoadingModel,
}

// Document Support List
const DocumentSupportList = optional(array(model('DocumentSupportList').props(documentSupportProps)), [])

const CertificationWebpageSupportDocumentModel = model('CertificationWebpageGeneralProof')
  .props({
    certificationWebpageSupportDocument_loading: withHelper_commonLoadingModel,
    certificationWebpageSupportDocument_list: DocumentSupportList,
  })
  .actions(self => ({
    certificationWebpageSupportDocument_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.certificationWebpageSupportDocument_loading, payload)

      detach(self.certificationWebpageSupportDocument_loading)
      self.certificationWebpageSupportDocument_loading = cast(map)
    },
    certificationWebpageSupportDocument_SET_LIST: (
      payload: ICertificationWebpageSupportDocumentApiListResponse['res']['data'],
    ): void => {
      self.certificationWebpageSupportDocument_list = cast(payload)
    },
    certificationWebpageSupportDocument_MAP_LOADING_LIST: (payload: ISharedCommonLoadingAttrs): void => {
      const map = cloneDeep(
        self.certificationWebpageSupportDocument_list.map(list => {
          if (list.userFileBlobId === payload.id) {
            return {
              ...list,
              loading: {
                ...list.loading,
                [payload.type]: payload.value,
              },
            }
          } else {
            return list
          }
        }),
      )

      detach(self.certificationWebpageSupportDocument_list)
      self.certificationWebpageSupportDocument_list = cast(map)
    },
    certificationWebpageSupportDocument_CLEAR_LIST: (): void => {
      self.certificationWebpageSupportDocument_list = cast([])
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch support document list
     *
     * @param {ICertificationWebpageSupportDocumentGetListAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageSupportDocument_fetchList: flow(function* (payload: ICertificationWebpageSupportDocumentGetListAttrs) {
      self.certificationWebpageSupportDocument_SET_LOADING({ type: BASE, value: true })

      const response: ICertificationWebpageSupportDocumentApiListResponse =
        yield self.environment.certificationWebpageSupportDocumentApi.certificationWebpageSupportDocument_getList({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        self.certificationWebpageSupportDocument_SET_LIST(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.certificationWebpageSupportDocument_SET_LOADING({ type: BASE, value: false })
    }),

    /**
     * @description Download document of support document
     *
     * @param {ICertificationWebpageSupportDocumentDownloadDocumentAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageSupportDocument_downloadDocument: flow(function* (
      payload: ICertificationWebpageSupportDocumentDownloadDocumentAttrs,
    ) {
      const { blobId: id } = payload.params

      self.certificationWebpageSupportDocument_MAP_LOADING_LIST({ id, type: DOWNLOAD, value: true })

      const response: ICertificationWebpageSupportDocumentApiDownloadDocumentResponse =
        yield self.environment.certificationWebpageSupportDocumentApi.certificationWebpageSupportDocument_downloadDocument({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        withHelper_fileDownload(response.res, response.headers!)

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.certificationWebpageSupportDocument_MAP_LOADING_LIST({ id, type: DOWNLOAD, value: false })
    }),
  }))

export const createCertificationWebpageSupportDocumentDefaultModel = () => optional(CertificationWebpageSupportDocumentModel, {})
